import { defineStore } from 'pinia';
import { $settings } from '@/provider/settings';
import axios from '@/axios';
import { useRootStore } from '@/store/root';
import { orderBy, isEmpty, deepClone } from '@/helper/lodashHelper';
import router from '@/router';
import { authService } from '@/services/auth';
import { deepReplaceObjectValue } from '@/helper/deepReplaceObjectValue';
import { isInOffice } from '@/utils/isInOffice';
import { addMailAttachmentsToBO } from '@/utils/upload';

const sapUrl = $settings.middlewareSAPProxyURI;

const remapPartnerArea = (type, items = []) =>
  items.map(({ alias, fields = [] }) => {
    const mappedFields =
      fields[0] &&
      fields[0].map((field) => ({
        ...field,
        type: field.wxtype,
        subType: field.parvw,
      }));

    const isContact = type === 'contact';
    const contentType = (isContact && 'organisationList') || 'threeSixtyView';

    return {
      alias,
      contentType,
      fields: mappedFields,
    };
  });

export const useObjectStore = (storeType) =>
  defineStore(`object-${storeType}`, {
    state: () => ({
      type: storeType,
      object: {},
      initObject: {},
      editObject: {},
      editInformation: {},
      editMode: false,
      overviewBar: {},
      roles: [],
      suggestions: [],
      suggestionsPartners: [],
      areaPattern: [],
      areaFields: [],
      availableAreas: [],
      searchCriteria: [],
      searchLists: [],
      searchAutoSuggest: [],
      titlePattern: '',
      tilePattern: {},
      tileFields: [],
      grouping: [],
      lists: {},
      keyFields: {},
      listPageSize: '',
      settings: {},
      globalSearch: {},
      texts: [],
      schema: {},
      progressBar: [],
      overviewGroups: [],
      viewAreas: {},
    }),
    getters: {
      getSchema: (state) => state.schema || {},
      getObject: (state) => state.object || {},
      getInitObject: (state) => state.initObject || {},
      getEditObject: (state) => state.editObject || {},
      getEditInformation: (state) => state.editInformation || {},
      getOverviewBar: (state) => state.overviewBar || [],
      getOverviewGroups: (state) => {
        if (!state.overviewGroups || state.overviewGroups.length === 0)
          return [];

        const modifiedOverviewBar = state.overviewBar.map(
          (overview, index) => ({
            ...overview,
            index,
          }),
        );

        return orderBy(
          state.overviewGroups.map((item) => ({
            ...item,
            overviewBar: orderBy(
              item.overviewBars?.map((overviewItem) => ({
                ...modifiedOverviewBar.find(({ id, type, subType }) => {
                  if (id !== undefined && overviewItem.id !== undefined) {
                    return id === overviewItem.id;
                  }
                  // fallback to type and subType mapping, though not recommended
                  return (
                    type === overviewItem.type &&
                    subType === overviewItem.subType
                  );
                }),
                ...overviewItem,
              })),
              'position',
            ),
          })),
          'position',
        );
      },
      getTitlePattern: (state) => state.titlePattern || '',
      getTilePattern:
        ({ tilePattern }) =>
        (subType) => {
          if (!tilePattern) return {};

          const path = subType && tilePattern[subType] ? subType : 'default';
          return tilePattern[path] || {};
        },
      getTileField: (state) => (path) => (altShape) =>
        state.tileFields
          .filter((tileField) => !!tileField[altShape])
          .map((tileField) => tileField[altShape])
          .find(({ fieldBind }) => fieldBind === path) || {},
      getAreaItemPattern: (state) => (area) =>
        state.areaPattern.find(({ areaType }) => areaType === area) || {},
      getAreaPattern: (state) => state.areaPattern || [],
      getAreaField: (state) => (field) => {
        const areFieldWithRecordBind = state.areaFields.find(
          ({ fieldBind, recordBind }) =>
            fieldBind === field.fieldBind && recordBind === field.recordBind,
        );

        return (
          areFieldWithRecordBind ||
          state.areaFields.find(
            ({ fieldBind }) => fieldBind === field.fieldBind,
          ) ||
          {}
        );
      },
      getAvailableAreas: (state) => state.availableAreas || [],
      getSuggestions:
        (state) =>
        ({ fieldBind, recordBind = '' }) =>
          state.suggestions.find((suggestion) => {
            if (recordBind) {
              return (
                suggestion.fieldBind === fieldBind &&
                suggestion.recordBind === recordBind
              );
            }
            return suggestion.fieldBind === fieldBind;
          }) || [],
      getGrouping: (state) => (altShape) => {
        const groupBy = state.grouping?.some(
          (groupOptions) => !!groupOptions[altShape],
        )
          ? altShape
          : 'default';

        return state.grouping?.reduce((accumulator, group) => {
          if (group[groupBy]) accumulator.push(group[groupBy]);
          return accumulator;
        }, []);
      },
      getLists: (state) => (listType) => (altShape) => {
        const listConfig = Object.entries(state.lists)
          .filter(([key]) => key.includes(listType || 'General'))
          .map(([, value]) => value);

        let list = listConfig[0];

        if (!list) return {};

        const path =
          altShape && list[altShape.toLowerCase()]
            ? altShape.toLowerCase()
            : 'default';

        list = list[path] || list;

        return { ...list, columns: orderBy(list.columns, 'position') };
      },
      getViewAreas: (state) => state.viewAreas || {},
      getViewArea: (state) => (area) => {
        let foundArea = [];

        const fallback = {
          DetailMain: 'detail',
        };

        Object.keys(state.viewAreas).some((viewArea) => {
          if (viewArea.search(area) !== -1) {
            foundArea = state.viewAreas[viewArea];
            return true;
          }
          return false;
        });

        return foundArea.map((tab) => {
          if (!tab.contentType) tab.contentType = fallback[area];
          return tab;
        });
      },
      getKeyField: (state) => (state.keyFields && state.keyFields.query) || '',
      getListPageSize: (state) => state.listPageSize,
      getSettings: (state) => state.settings || {},
      getProgressBar: (state) => state.progressBar || [],
      getSearchAutoSuggest: (state) => (field) =>
        state.searchAutoSuggest.find(
          ({ recordBind, fieldBind }) =>
            recordBind === field.recordBind && fieldBind === field.fieldBind,
        ) || {},
      getSearchCriteria: (state) => state.searchCriteria || [],
      getTexts: (state) => state.texts || [],
      getExpSearch: (state) => state.expSearch || {},
    },
    actions: {
      async fetchObject({ key, type }) {
        try {
          const { data } =
            (await axios({ url: [sapUrl, type, key].join('/') })) || {};
          const { SUCCESS, DATA } = data;

          if (!SUCCESS || !DATA) router.push({ name: 'error-404' });

          deepReplaceObjectValue(DATA, '00:00:00', null);

          this.object = deepClone(DATA);
        } catch (error) {
          console.warn(`Could not load partners data:`, error);
          const redirect = error.status === 403 ? 'error-403' : 'error-404';
          router.push({ name: redirect });
        }
      },
      async resetObject() {
        this.object = {};
      },
      async getForEdit({ type, key, method }) {
        const useType = type || useRootStore().parentObject.type;

        const getUpdateResponse = async () => {
          const response = await axios({
            url: `${[sapUrl, useType, key].join('/')}?ForEdit=X`,
          });

          const object = response.data;
          // Todo: BE should provide full structure
          if (type === 'opportunity' && object.DATA.DEBOPP_TEXTS.length === 0) {
            object.DATA.DEBOPP_TEXTS.push({
              MODIFY_FLAG: '',
              TDOBJECT: '',
              TDID: '',
              TDNAME: '',
              TDSPRAS: '',
              DESCR: '',
              TEXTSTRING: '',
            });
          }

          return object;
        };
        const getCopyResponse = async () => {
          const response = await axios({
            url: `${[sapUrl, useType, 'copy', key].join('/')}`,
          });

          return (response && response.data) || {};
        };

        const object =
          method === 'copy'
            ? await getCopyResponse()
            : await getUpdateResponse();

        this.setEditObject(object.DATA);
      },
      async initObject(parameter) {
        try {
          const response = await axios({
            method: 'POST',
            url: [sapUrl, this.type, 'init'].join('/'),
            data: parameter || {},
          });

          if (response && response.data) {
            const object = response.data;

            this.setEditObject(object.DATA);

            if (!response.data.SUCCESS) {
              throw new Error(`Error: ${response.data.MESSAGES}`);
            }
          }
        } catch (error) {
          throw new Error(error);
        }
      },
      setEditObject(object) {
        this.editObject = object;
      },
      setEditInformation(information) {
        this.editInformation = {
          ...this.editInformation,
          ...information,
        };
      },
      resetEditInformation() {
        this.editInformation = {};
      },
      async fetchSchemaForBO() {
        const { schema, type } = this;

        if (isEmpty(schema)) {
          try {
            const response = await axios({
              url: [sapUrl, type, 'schema'].join('/'),
            });

            const { components = {} } = response.data;

            this.schema = components;
          } catch (error) {
            throw new Error(error);
          }
        }
      },
      async augmentDataWithBearer({ value }) {
        const token = await authService.getGraphToken();

        Object.keys(value).some((recordBind) => {
          if ('BEARER_TOKEN' in value[recordBind]) {
            value[recordBind].BEARER_TOKEN = token.accessToken;
            return true;
          }
          return false;
        });

        return value;
      },
      async updateObject({ type, key, value }) {
        const { data } = await axios({
          method: 'PUT',
          url: [sapUrl, type, key].join('/'),
          data: await this.augmentDataWithBearer({ value }),
        });

        this.fetchObject({ key, type });

        return data.OBJKEY;
      },
      async createObject(item) {
        const response = await axios({
          method: 'POST',
          url: [sapUrl, item.type].join('/'),
          data: await this.augmentDataWithBearer(item),
        });

        if (isInOffice()) {
          const { OBJKEY, OBJTYPE } = response.data;
          await addMailAttachmentsToBO({
            key: OBJKEY,
            type: OBJTYPE,
          }).catch((error) => {
            console.error(error);
          });
        }

        return response?.data?.OBJKEY;
      },
      async fetchProgressBar() {
        const { type, key } = useRootStore().getParentObject;
        const response = await axios({
          url: `${[sapUrl, type, 'progressbar', key].join('/')}`,
        });

        if (response && response.data) this.progressBar = response.data.DATA;

        return Promise.reject;
      },
      async fetchSuggestions({ source, params }) {
        const { data } = await axios({
          method: 'post',
          url: [sapUrl, this.type, 'autosuggest'].join('/'),
          data: { SOURCE: source, REFS: params || [] },
        });

        return data || [];
      },
      setViewAreas(viewAreas) {
        const mappedViewAreas = {};

        viewAreas &&
          Object.entries(viewAreas).forEach(([key, items]) => {
            const isPartnerArea = key.includes('DetailPartners');
            mappedViewAreas[key] = isPartnerArea
              ? remapPartnerArea(this.type, items)
              : items;
          });

        if (this.type === 'email') {
          // Todo: this should come from metadata
          mappedViewAreas.vaMailDetailAttachments = [
            {
              alias: 'WX_GENERAL_ATTACHMENTS',
              contentType: 'attachments',
              fields: [],
            },
          ];
        }

        this.viewAreas = mappedViewAreas;
      },
      // TODO: BE should provide same structure here as for searchAutosuggest
      setSuggestions(suggestions = []) {
        this.suggestions = suggestions.map((suggestion) => ({
          fieldBind: suggestion.FIELDNAME,
          recordBind: suggestion.RECORD,
          numChars: suggestion.NUM_CHARS,
          inputMap: suggestion.INPUT_MAP,
          output: suggestion.output,
          freeText: suggestion.freeText,
          multi: suggestion.multi,
          targets: suggestion.targets,
        }));
      },
      init({ meta }) {
        this.overviewGroups = meta.overviewGroups;
        this.overviewBar = meta.overviewBar;
        this.titlePattern = meta.titlePattern;
        this.tilePattern = meta.tilePattern;
        this.tileFields = meta.tileFields;
        this.areaPattern = meta.areaPattern || [];
        this.availableAreas = meta.areaPattern?.map(({ areaType }) => areaType);
        this.areaFields = meta.areaFields || [];
        this.grouping = meta.tileGrouping;
        this.lists = meta.lists;
        this.setViewAreas(meta.viewAreas);
        this.keyFields = meta.keyFields;
        this.searchCriteria = meta.searchCriteria;
        this.searchLists = meta.searchLists;
        this.searchAutoSuggest = meta.searchAutoSuggest;
        this.setSuggestions(meta.autoSuggest);
        this.listPageSize = meta.pageSize;
        this.settings = meta.general;
        this.globalSearch = meta.globalSearch;
        this.texts = meta.texts;
        this.expSearch = meta.expSearch;
      },
    },
  })();
